import { h } from "@/lib/dom";
import { button, openModal } from "@/components/ui";

const SVG_NS = "http://www.w3.org/2000/svg";
const WIDTH = 900;
const HEIGHT = 600;
const COLORS = ["#1a1a1a", "#e11d48", "#2563eb", "#16a34a", "#f59e0b"];
const SIZES: { label: string; px: number }[] = [
  { label: "S", px: 2 },
  { label: "M", px: 5 },
  { label: "L", px: 10 },
];
const HOLD_MS = 3000;
const JITTER_PX = 6;

type Point = { x: number; y: number };

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

function svgPoint(svg: SVGSVGElement, e: PointerEvent): Point {
  const pt = svg.createSVGPoint();
  pt.x = e.clientX;
  pt.y = e.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function centroidOf(points: Point[]): Point {
  const n = points.length;
  return { x: points.reduce((s, p) => s + p.x, 0) / n, y: points.reduce((s, p) => s + p.y, 0) / n };
}

function bboxOf(points: Point[]): { x: number; y: number; w: number; h: number } {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

type Shape =
  | { tag: "line"; attrs: Record<string, string | number> }
  | { tag: "circle"; attrs: Record<string, string | number> }
  | { tag: "rect"; attrs: Record<string, string | number> };

/**
 * Goodnotes-artige Formerkennung: reicht nur für "sieht sauber danach aus",
 * kein echter Formenerkenner. Reihenfolge zählt – ein geschlossener, runder
 * Umriss ist ein Kreis; geschlossen und eher eckig wird zum Rechteck über der
 * Umrandungsbox statt einer echten Eckenerkennung; sonst zählt, wie gerade
 * der Weg von Anfang zu Ende war.
 */
function recognizeShape(points: Point[], color: string, size: number): Shape | null {
  if (points.length < 6) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const box = bboxOf(points);
  const span = Math.max(box.w, box.h);
  if (span < 16) return null;
  const closed = dist(first, last) < span * 0.25;
  const aspect = box.w > 0 && box.h > 0 ? Math.min(box.w, box.h) / Math.max(box.w, box.h) : 0;

  if (closed && aspect > 0.7) {
    const c = centroidOf(points);
    const radii = points.map((p) => dist(p, c));
    const avg = radii.reduce((a, r) => a + r, 0) / radii.length;
    const roundness = radii.reduce((a, r) => a + Math.abs(r - avg), 0) / radii.length / avg;
    if (roundness < 0.22) {
      return { tag: "circle", attrs: { cx: c.x, cy: c.y, r: avg, stroke: color, "stroke-width": size, fill: "none" } };
    }
    return { tag: "rect", attrs: { x: box.x, y: box.y, width: box.w, height: box.h, stroke: color, "stroke-width": size, fill: "none" } };
  }

  const lineLen = dist(first, last);
  if (lineLen < span * 0.7) return null;
  const dx = last.x - first.x;
  const dy = last.y - first.y;
  const normSq = dx * dx + dy * dy;
  const maxDev = Math.max(
    ...points.map((p) => {
      const t = ((p.x - first.x) * dx + (p.y - first.y) * dy) / normSq;
      const projX = first.x + t * dx;
      const projY = first.y + t * dy;
      return dist(p, { x: projX, y: projY });
    })
  );
  if (maxDev > Math.max(8, lineLen * 0.06)) return null;
  return { tag: "line", attrs: { x1: first.x, y1: first.y, x2: last.x, y2: last.y, stroke: color, "stroke-width": size, "stroke-linecap": "round" } };
}

export interface DrawDialogOptions {
  /** Vorhandene Skizze zum Weiterbearbeiten (roher SVG-Quelltext). */
  initialSvg?: string;
  /** Bekommt das fertige Bild (SVG). */
  onSave: (blob: Blob) => void;
}

export function openDrawDialog(opts: DrawDialogOptions): void {
  const svg = svgEl("svg", {
    width: WIDTH,
    height: HEIGHT,
    viewBox: `0 0 ${WIDTH} ${HEIGHT}`,
    class: "draw-canvas",
    xmlns: SVG_NS,
  });
  const bg = svgEl("rect", { x: 0, y: 0, width: WIDTH, height: HEIGHT, fill: "#ffffff" });
  svg.appendChild(bg);

  if (opts.initialSvg) {
    try {
      const parsed = new DOMParser().parseFromString(opts.initialSvg, "image/svg+xml");
      for (const node of Array.from(parsed.documentElement.children)) {
        if (node.tagName.toLowerCase() === "rect" && node === parsed.documentElement.firstElementChild) continue;
        svg.appendChild(document.importNode(node, true));
      }
    } catch {
      /* kaputtes SVG – einfach leer starten statt abzubrechen */
    }
  }

  let color = COLORS[0];
  let size = SIZES[1].px;
  let erasing = false;
  const undoStack: SVGElement[] = [];

  let active: SVGElement | null = null;
  let points: Point[] = [];
  let pathD = "";
  let holdTimer: number | undefined;
  let holdAnchor: Point = { x: 0, y: 0 };

  const endStroke = () => {
    window.clearTimeout(holdTimer);
    if (!active) return;
    undoStack.push(active);
    undoBtn.disabled = false;
    active = null;
    points = [];
  };

  const trySnap = () => {
    if (!active) return;
    const shape = recognizeShape(points, color, size);
    if (!shape) {
      holdAnchor = points[points.length - 1] ?? holdAnchor;
      holdTimer = window.setTimeout(trySnap, HOLD_MS);
      return;
    }
    active.remove();
    active = svgEl(shape.tag, shape.attrs);
    svg.appendChild(active);
    endStroke();
  };

  const armHold = () => {
    window.clearTimeout(holdTimer);
    holdTimer = window.setTimeout(trySnap, HOLD_MS);
  };

  svg.addEventListener("pointerdown", (e) => {
    const p = svgPoint(svg, e);
    if (erasing) {
      const hit = (e.target as Element).closest("path,line,circle,rect") as SVGGraphicsElement | null;
      if (hit && hit !== bg && hit.parentNode === svg) hit.remove();
      return;
    }
    svg.setPointerCapture(e.pointerId);
    points = [p];
    holdAnchor = p;
    pathD = `M ${p.x} ${p.y}`;
    active = svgEl("path", {
      d: pathD,
      stroke: color,
      "stroke-width": size,
      fill: "none",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
    });
    svg.appendChild(active);
    armHold();
  });

  svg.addEventListener("pointermove", (e) => {
    if (!active) return;
    const p = svgPoint(svg, e);
    points.push(p);
    pathD += ` L ${p.x} ${p.y}`;
    active.setAttribute("d", pathD);
    if (dist(p, holdAnchor) > JITTER_PX) {
      holdAnchor = p;
      armHold();
    }
  });

  svg.addEventListener("pointerup", endStroke);
  svg.addEventListener("pointerleave", endStroke);

  const undoBtn = button({
    icon: "undo",
    variant: "ghost",
    size: "sm",
    title: "Rückgängig",
    disabled: true,
    onClick: () => {
      undoStack.pop()?.remove();
      undoBtn.disabled = !undoStack.length;
    },
  });

  const eraserBtn = h(
    "button",
    {
      type: "button",
      class: "draw-size-btn",
      title: "Radierer – auf eine Form tippen, um sie zu löschen",
      onclick: () => {
        erasing = !erasing;
        eraserBtn.classList.toggle("is-active", erasing);
      },
    },
    "🧽"
  );

  const colorBtns = COLORS.map((c) => {
    const b = h("button", {
      type: "button",
      class: "draw-swatch" + (c === color ? " is-active" : ""),
      style: `background:${c}`,
      title: "Farbe",
      onclick: () => {
        color = c;
        colorBtns.forEach((x, i) => x.classList.toggle("is-active", COLORS[i] === c));
      },
    });
    return b;
  });

  const sizeBtns = SIZES.map((s) => {
    const b = h(
      "button",
      {
        type: "button",
        class: "draw-size-btn" + (s.px === size ? " is-active" : ""),
        onclick: () => {
          size = s.px;
          sizeBtns.forEach((x, i) => x.classList.toggle("is-active", SIZES[i].px === s.px));
        },
      },
      s.label
    );
    return b;
  });

  const clearBtn = button({
    label: "Alles löschen",
    variant: "ghost",
    size: "sm",
    onClick: () => {
      for (const child of Array.from(svg.children)) if (child !== bg) child.remove();
      undoStack.length = 0;
      undoBtn.disabled = true;
    },
  });

  const modal = openModal({
    title: opts.initialSvg ? "Skizze bearbeiten" : "Skizze zeichnen",
    wide: true,
    guardUnsaved: false,
    body: h(
      "div",
      { style: "display:flex;flex-direction:column;gap:10px" },
      h(
        "div",
        { style: "display:flex;align-items:center;gap:6px;flex-wrap:wrap" },
        eraserBtn,
        h("span", { class: "tool-sep" }),
        ...colorBtns,
        h("span", { class: "tool-sep" }),
        ...sizeBtns,
        h("span", { class: "tool-sep" }),
        undoBtn,
        clearBtn
      ),
      h("div", { class: "draw-stage" }, svg),
      h("p", { class: "muted", style: "font-size:12px;margin:0" }, "Zeichnen und kurz halten (3 Sek.) formt Strich, Kreis oder Rechteck automatisch gerade.")
    ),
    footer: h(
      "div",
      { class: "row" },
      button({ label: "Abbrechen", variant: "ghost", onClick: () => modal.close() }),
      button({
        label: "Einfügen",
        variant: "primary",
        onClick: () => {
          const source = new XMLSerializer().serializeToString(svg);
          opts.onSave(new Blob([source], { type: "image/svg+xml" }));
          modal.close();
        },
      })
    ),
  });
}
